// @flow
import {debounceTime, map, mergeMap, switchMap} from 'rxjs/operators';
import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {ofType} from 'redux-observable';

import {
  createTaxApi,
  getTaxesApi,
  endpoints,
  getTaxApi,
  updateTaxApi,
  getProductTaxesApi,
  geTaxSettingApi,
} from './api';
import {buildRoute} from '../../../http';
import {GetSessionSuccess, LoginSuccess} from '../../auth/duck';

import type {TaxRequest} from './api';
import type {RootState} from '../../index';
import type {Tax, TaxSetting} from '../../../entities';
import type {ActionsObservable, StateObservable} from 'redux-observable';
import type {Action} from '../../../store/utils';

export type TaxFilterState = '0' | '1';
export type TaxesAction = Action & {
  taxRequest?: TaxRequest,
  taxFilterState?: TaxFilterState,
  id?: string,
};
export type TaxesConfigState = {
  isSaving?: boolean,
  taxSetting?: TaxSetting,
  isFetching?: boolean,
  isFetchingTaxSetting?: boolean,
  taxes?: Tax[],
  tax?: Tax,
  saveSuccess?: boolean,
  taxFilterState?: string,
  isFetchingTaxes?: boolean,
  taxesMap: {[key: string]: any},
};

export class FetchTaxSetting extends ActionCreator<TaxesAction> {}
export class FetchTaxSettingSuccess extends ActionCreator<TaxesAction> {}
export class FetchTaxSettingError extends ActionCreator<TaxesAction> {}

export class FetchTaxes extends ActionCreator<TaxesAction> {}
export class FetchTaxesSuccess extends ActionCreator<TaxesAction> {}
export class FetchTaxesError extends ActionCreator<TaxesAction> {}

export class FetchTax extends ActionCreator<TaxesAction> {}
export class FetchTaxSuccess extends ActionCreator<TaxesAction> {}
export class FetchTaxError extends ActionCreator<TaxesAction> {}

export class CreateTax extends ActionCreator<TaxesAction> {}
export class CreateTaxSuccess extends ActionCreator<TaxesAction> {}
export class CreateTaxError extends ActionCreator<TaxesAction> {}

export class UpdateTax extends ActionCreator<TaxesAction> {}
export class UpdateTaxSuccess extends ActionCreator<TaxesAction> {}
export class UpdateTaxError extends ActionCreator<TaxesAction> {}

export class FetchProductTaxes extends ActionCreator<TaxesAction> {}
export class FetchProductTaxesSuccess extends ActionCreator<TaxesAction> {}
export class FetchProductTaxesErrorFn extends ActionCreator<TaxesAction> {}

export class ResetTaxesState extends ActionCreator<TaxesAction> {}

export class ResetTax extends ActionCreator<TaxesAction> {}

export class ToggleTaxesFilter extends ActionCreator<TaxesAction> {}

export const actions = {
  fetchTaxSetting: (payload: TaxesAction) => new FetchTaxSetting(payload),
  createTax: (payload: TaxesAction) => new CreateTax(payload),
  fetchTaxes: (payload: TaxesAction) => new FetchTaxes(payload),
  fetchTax: (payload: TaxesAction) => new FetchTax(payload),
  updateTax: (payload: TaxesAction) => new UpdateTax(payload),
  toggleTaxesFilter: (payload: TaxesAction) => new ToggleTaxesFilter(payload),
  fetchProductTaxes: (payload: TaxesAction) => new FetchProductTaxes(payload),
  resetState: () => new ResetTaxesState(),
  resetTax: () => new ResetTax(),
};

export const initialState = {taxFilterState: '0', isFetchingTaxSetting: true};

export default createReducer<TaxesConfigState>(initialState, {
  [FetchTaxSettingSuccess.type]: (
    state: TaxesConfigState,
    action: TaxesAction
  ) => ({
    ...state,
    isFetchingTaxSetting: false,
    taxSetting: action.taxSetting,
  }),
  [FetchTaxSettingError.type]: (
    state: TaxesConfigState,
    action: TaxesAction
  ) => ({
    ...state,
    isFetchingTaxSetting: false,
  }),
  [FetchTaxes.type]: (state: TaxesConfigState) => ({
    ...state,
    isFetching: true,
  }),
  [FetchTaxesSuccess.type]: (state: TaxesConfigState, action: TaxesAction) => ({
    ...state,
    isFetching: undefined,
    taxes: action.data,
  }),
  [FetchTaxesError.type]: (state: TaxesConfigState, action: TaxesAction) => ({
    ...state,
    isFetching: undefined,
    error: action.error,
  }),
  [FetchTax.type]: (state: TaxesConfigState) => ({
    ...state,
    isFetching: true,
  }),
  [FetchTaxSuccess.type]: (state: TaxesConfigState, action: TaxesAction) => ({
    ...state,
    isFetching: undefined,
    tax: action.data,
  }),
  [FetchTaxError.type]: (state: TaxesConfigState, action: TaxesAction) => ({
    ...state,
    isFetching: undefined,
    error: action.error,
  }),
  [CreateTax.type]: (state: TaxesConfigState) => ({
    ...state,
    isSaving: true,
  }),
  [CreateTaxSuccess.type]: (state: TaxesConfigState, action: TaxesAction) => ({
    ...state,
    isSaving: undefined,
    saveSuccess: true,
  }),
  [CreateTaxError.type]: (state: TaxesConfigState, action: TaxesAction) => ({
    ...state,
    isSaving: undefined,
    saveSuccess: undefined,
    error: action.error,
  }),
  [UpdateTax.type]: (state: TaxesConfigState) => ({
    ...state,
    isSaving: true,
  }),
  [UpdateTaxSuccess.type]: (state: TaxesConfigState, action: TaxesAction) => ({
    ...state,
    isSaving: undefined,
    saveSuccess: true,
  }),
  [UpdateTaxError.type]: (state: TaxesConfigState, action: TaxesAction) => ({
    ...state,
    isSaving: undefined,
    saveSuccess: undefined,
    error: action.error,
  }),
  [ToggleTaxesFilter.type]: (state: TaxesConfigState, action: TaxesAction) => ({
    ...state,
    taxFilterState: action.taxFilterState || '',
  }),
  [ResetTax.type]: (state: TaxesConfigState) => ({
    ...state,
    tax: undefined,
  }),
  [ResetTaxesState.type]: (state: TaxesConfigState) => ({
    ...state,
    saveSuccess: undefined,
    error: undefined,
  }),
  [FetchProductTaxes.type]: (state: TaxesConfigState, action: TaxesAction) => {
    let taxesMap = state.taxesMap || {};
    taxesMap[action.productId] = {};
    return {
      ...state,
      taxesMap,
      isFetchingTaxes: true,
    };
  },
  [FetchProductTaxesSuccess.type]: (
    state: TaxesConfigState,
    action: TaxesAction
  ) => {
    let taxesMap = state.taxesMap || {};
    if (action.productId && taxesMap) {
      taxesMap = {
        ...taxesMap,
        [action.productId]: action.taxes,
      };
    }
    return {
      ...state,
      isFetchingTaxes: false,
      taxesMap,
    };
  },
  [FetchProductTaxesErrorFn.type]: (
    state: TaxesConfigState,
    action: TaxesAction
  ) => ({
    ...state,
    isFetchingTaxes: false,
  }),
});

export const fetchTaxesSettingEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(LoginSuccess.type, GetSessionSuccess.type),
    mergeMap(({storeId, productId}: TaxesAction) =>
      geTaxSettingApi().pipe(
        map(
          (taxSetting: TaxSetting) => new FetchTaxSettingSuccess({taxSetting})
        ),
        buildCatchError(FetchTaxSettingError)
      )
    )
  );

export function createTaxEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(CreateTax.type),
    switchMap(({taxRequest}: TaxesAction) => {
      return createTaxApi(taxRequest).pipe(
        map((data: Tax) => new CreateTaxSuccess({data})),
        buildCatchError(CreateTaxError, 'Error al crear impuesto.')
      );
    })
  );
}

export function updateTaxEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(UpdateTax.type),
    switchMap(({taxId, taxRequest}: TaxesAction) => {
      return updateTaxApi(taxRequest, {
        route: buildRoute(endpoints.GET_TAX, [taxId]),
      }).pipe(
        map((data: Tax) => new UpdateTaxSuccess({data})),
        buildCatchError(UpdateTaxError, 'Error al actualizar impuesto.')
      );
    })
  );
}

export function fetchTaxesEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchTaxes.type, CreateTaxSuccess.type, UpdateTaxSuccess.type),
    switchMap(() => {
      const selectedState = state$.value.taxesConfig.taxFilterState;
      const queryFilter =
        selectedState !== ''
          ? selectedState === '0'
            ? {enabled: '1'}
            : {enabled: '0'}
          : {};
      return getTaxesApi(queryFilter).pipe(
        map((data: Tax[]) => new FetchTaxesSuccess({data})),
        buildCatchError(FetchTaxesError, 'Error al obtener impuestos.')
      );
    })
  );
}

export const fetchTaxEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchTax.type),
    debounceTime(250),
    switchMap(({taxId}: TaxesAction) =>
      getTaxApi(undefined, {
        route: buildRoute(endpoints.GET_TAX, [taxId]),
      }).pipe(
        map(data => new FetchTaxSuccess({data})),
        buildCatchError(FetchTaxError)
      )
    )
  );

export const fetchProductTaxesEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(FetchProductTaxes.type),
    mergeMap(({storeId, productId}: TaxesAction) =>
      getProductTaxesApi(
        {
          store_id: storeId,
        },
        {route: buildRoute(endpoints.GET_PRODUCT_TAXES, [productId])}
      ).pipe(
        map(taxes => new FetchProductTaxesSuccess({productId, taxes})),
        buildCatchError(FetchProductTaxesErrorFn)
      )
    )
  );
